import { parseDocument } from 'htmlparser2'
import type { LegacyExtractedTemplateData, LegacyParsedNode, LegacyTemplateData, LegacyTemplateInfo } from './LegacyHtmlAstTypes'

function collect(node: any, data: LegacyTemplateData | null, state: { element: number; prefix: string; preserveExistingSlots: boolean }): LegacyParsedNode | null {
  if (node.type === 'text') {
    data?.text.push(node.data || '')
    return { kind: 'text', value: node.data || '' }
  }

  if (node.type !== 'tag' && node.type !== 'script' && node.type !== 'style') return null

  const rawAttribs = { ...(node.attribs || {}) }
  if (!state.preserveExistingSlots) delete rawAttribs['data-md-slot']

  const existingSlot = state.preserveExistingSlots ? node.attribs?.['data-md-slot'] : null
  const slot = existingSlot || `${state.prefix}${state.element++}`
  const attrs = Object.entries(rawAttribs).map(([name, value]) => ({
    name,
    value: String(value),
  }))

  for (const value of Object.values(rawAttribs)) {
    data?.attrs.push(String(value))
  }

  const children: LegacyParsedNode[] = []
  for (const child of node.children || []) {
    const parsedChild = collect(child, data, state)
    if (parsedChild) children.push(parsedChild)
  }

  if (data && !data.answerSubs && node.name === 'ul' && node.attribs?.id === 'mcss-answers_ul-cssm') {
    data.answerSubs = children
  }

  return {
    kind: 'element',
    tag: node.name.toLowerCase(),
    slot,
    attrs,
    children,
  }
}

export function parseLegacyHtmlFragmentNodes(html: string, slotStart = 0, options: { preserveExistingSlots?: boolean } = {}): LegacyParsedNode[] {
  const doc = parseDocument(html, { recognizeSelfClosing: true })
  const nodes = doc.children
  const state = { element: slotStart, prefix: 'rx', preserveExistingSlots: options.preserveExistingSlots !== false }

  return nodes
    .map(node => collect(node, null, state))
    .filter((node): node is LegacyParsedNode => !!node)
}

function collectTemplateData(node: any, data: LegacyExtractedTemplateData): void {
  if (node.type === 'text') {
    data.text.push(node.data || '')
    return
  }

  if (node.type !== 'tag' && node.type !== 'script' && node.type !== 'style') return

  data.elementSlots += 1
  const attrs = Object.entries(node.attribs || {}).filter(([name]) => name !== 'data-md-slot')
  for (const [, value] of attrs) {
    data.attrs.push(String(value))
  }

  for (const child of node.children || []) {
    collectTemplateData(child, data)
  }
}

function signatureForTemplateNode(node: any): string {
  if (node.type === 'text') return '#text'
  if (node.type !== 'tag' && node.type !== 'script' && node.type !== 'style') return ''
  const attrs = Object.keys(node.attribs || {}).filter(name => name !== 'data-md-slot')
  return `<${node.name.toLowerCase()}[${attrs.join(',')}]>${(node.children || []).map(signatureForTemplateNode).join('')}</${node.name.toLowerCase()}>`
}

export function extractNewEditorTemplateData(html: string, expected?: LegacyTemplateInfo): LegacyExtractedTemplateData | null {
  const doc = parseDocument(html, { recognizeSelfClosing: true })
  const nodes = doc.children
  const data: LegacyExtractedTemplateData = {
    attrs: [],
    text: [],
    rootNodes: nodes.length,
    elementSlots: 0,
    attrSlots: 0,
    textSlots: 0,
    signatureLength: 0,
    signature: '',
  }

  for (const node of nodes) {
    collectTemplateData(node, data)
  }

  data.attrSlots = data.attrs.length
  data.textSlots = data.text.length
  data.signature = nodes.map(signatureForTemplateNode).join('')
  data.signatureLength = data.signature.length

  if (!expected) return data
  if (data.rootNodes !== expected.rootNodes) return null
  if (data.elementSlots !== expected.elementSlots) return null
  if (data.attrSlots !== expected.attrSlots) return null
  if (data.textSlots !== expected.textSlots) return null
  if (data.signatureLength !== expected.signatureLength) return null

  return data
}
